import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, Download, Check } from 'lucide-react';
import { blogArticles, featurePages, comparisonPages, useCasePages } from '../data/knowledgeHub';
import { Helmet } from 'react-helmet-async';

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.typeit4u.keyboard";

const BlogArticle = () => {
  const { slug } = useParams();
  
  // Check all article types
  let article = blogArticles.find(a => a.slug === slug);
  let articleType = 'blog';
  
  if (!article) {
    article = featurePages.find(a => a.slug === slug);
    articleType = 'feature';
  }
  if (!article) {
    article = comparisonPages.find(a => a.slug === slug);
    articleType = 'comparison';
  }
  if (!article) {
    article = useCasePages.find(a => a.slug === slug);
    articleType = 'usecase';
  }

  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 container text-center">
          <h1 className="text-3xl font-bold mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-orange-600 hover:underline">Back to Blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const backLink = articleType === 'blog' ? '/blog' : '/features';

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{article.title} | TypeIt4U</title>
        <meta name="description" content={article.description} />
        <meta name="keywords" content={article.keywords?.join(', ')} />
        <link rel="canonical" href={`https://typeit4u.com/blog/${article.slug}`} />
      </Helmet>

      <Navbar />
      
      {/* Breadcrumb */}
      <section className="pt-28 pb-4 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <Link to={backLink} className="inline-flex items-center gap-2 text-gray-600 hover:text-orange-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      </section>

      {/* Article Content */}
      <section className="pb-20 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container max-w-3xl">
          <motion.article
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
          >
            {/* Header */}
            <div className="mb-8">
              {article.category && (
                <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-4">
                  {article.category}
                </span>
              )}
              <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
                {article.title}
              </h1>
              <p className="text-lg text-gray-600">{article.description}</p>
            </div>

            {/* Blog Content */}
            {articleType === 'blog' && article.content && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                <p className="text-gray-700 leading-relaxed mb-6">{article.content}</p>
                
                {article.sections?.map((section, index) => (
                  <div key={index} className="mb-6 last:mb-0">
                    <h2 className="text-xl font-semibold mb-3" style={{ color: '#0A0A0A' }}>
                      {section.title}
                    </h2>
                    <ul className="space-y-2">
                      {section.points.map((point, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700">
                          <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#FF6B9D' }} />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Feature Page Content */}
            {articleType === 'feature' && (
              <>
                {article.hero && (
                  <div className="bg-gradient-to-r from-orange-100 to-pink-100 rounded-xl p-8 mb-8 text-center">
                    <p className="text-xl font-medium" style={{ color: '#0A0A0A' }}>{article.hero}</p>
                  </div>
                )}
                
                {article.benefits && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                    <h2 className="text-xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>Benefits</h2>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {article.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-700">
                          <Check className="w-5 h-5" style={{ color: '#FF6B9D' }} />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {article.howItWorks && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                    <h2 className="text-xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>How It Works</h2>
                    <div className="space-y-3">
                      {article.howItWorks.map((step, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <span 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                            style={{ background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)' }}
                          >
                            {i + 1}
                          </span>
                          <span className="text-gray-700">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Comparison Page Content */}
            {articleType === 'comparison' && article.comparison && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-8">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-orange-100 to-pink-100">
                      <th className="text-left p-4 font-semibold">Feature</th>
                      <th className="text-center p-4 font-semibold" style={{ color: '#FF6B9D' }}>TypeIt4U</th>
                      <th className="text-center p-4 font-semibold text-gray-600">{article.competitor}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {article.comparison.map((row, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="p-4 text-gray-800">{row.feature}</td>
                        <td className="p-4 text-center">
                          {row.typeit4u === true ? <Check className="w-5 h-5 text-green-600 mx-auto" /> : 
                           typeof row.typeit4u === 'string' ? <span className="text-green-600 font-medium">{row.typeit4u}</span> : null}
                        </td>
                        <td className="p-4 text-center text-gray-500">
                          {row.competitor === true ? <Check className="w-5 h-5 text-green-600 mx-auto" /> :
                           row.competitor === false ? <span className="text-red-400">✗</span> :
                           <span>{row.competitor}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {article.verdict && (
                  <div className="p-6 bg-orange-50 border-t border-orange-100">
                    <p className="font-medium text-gray-800"><strong>Verdict:</strong> {article.verdict}</p>
                  </div>
                )}
              </div>
            )}

            {/* Use Case Page Content */}
            {articleType === 'usecase' && (
              <>
                {article.benefits && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                    <h2 className="text-xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>
                      Why TypeIt4U for {article.audience}
                    </h2>
                    <ul className="space-y-2">
                      {article.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-700">
                          <Check className="w-5 h-5" style={{ color: '#FF6B9D' }} />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {article.useCases && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                    <h2 className="text-xl font-semibold mb-4" style={{ color: '#0A0A0A' }}>Use Cases</h2>
                    <div className="grid grid-cols-2 gap-3">
                      {article.useCases.map((useCase, i) => (
                        <div key={i} className="bg-orange-50 rounded-lg p-3 text-sm text-gray-700">
                          {useCase}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Disclaimer for comparison pages */}
            {articleType === 'comparison' && (
              <div className="bg-gray-50 rounded-xl p-4 mb-8 text-xs text-gray-500">
                <strong>Disclaimer:</strong> {article.competitor} is a trademark of its respective owner. TypeIt4U is an independent product and is not affiliated with or endorsed by {article.competitor} or its parent company.
              </div>
            )}

            {/* CTA */}
            <div className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-xl p-8 text-center border border-orange-100">
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A0A0A' }}>
                Ready to Try TypeIt4U?
              </h3>
              <p className="text-gray-600 mb-6">
                Download now and experience India's smartest AI keyboard.
              </p>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                  color: 'white',
                  borderRadius: '26px',
                  padding: '14px 28px',
                  fontSize: '16px',
                  fontWeight: 500
                }}
              >
                <Download className="w-5 h-5" />
                Download TypeIt4U
              </a>
            </div>
          </motion.article>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogArticle;
